import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  Typography,
  Button,
  Box,
  Input,
  FormControl,
  FormLabel,
  CircularProgress,
  Alert,
  IconButton,
  LinearProgress,
} from '@mui/joy';
import { UploadSimple as Upload, X, Camera, FileImage, Check, XCircle as AlertCircle, ArrowsClockwise as RefreshCw } from '@phosphor-icons/react';
import { usePhotoBatchUpload, type BatchUploadProgress } from '../hooks/usePhotoBatchUpload';
import { TagPicker } from './tags/TagPicker';
import { useTags, useCreateTag } from '../hooks/useTags';
import type { Tag as TagType } from '../types';

interface PhotoUploadModalProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
}

// Get status icon for a file based on upload progress
const getFileStatus = (
  file: File,
  index: number,
  progress: BatchUploadProgress
): 'pending' | 'uploading' | 'success' | 'failed' => {
  if (progress.status === 'idle') return 'pending';
  if (progress.status === 'creating_batch') return 'pending';

  // Check if this file failed
  const failedFile = progress.failedFiles.find(f => f.file.name === file.name);
  if (failedFile) return 'failed';

  // Check if currently uploading this file
  if (progress.currentFile === file.name) return 'uploading';

  // Check if this file was already uploaded (index < uploaded count)
  // Note: We count both uploaded and failed files processed so far
  const processedCount = progress.uploaded + progress.failed;

  // If we're past this file's position and it didn't fail, it succeeded
  if (index < processedCount && !failedFile) return 'success';

  return 'pending';
};

export const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
  open,
  onClose,
  jobId,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [description, setDescription] = useState('');
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { progress, uploadPhotos, retryFailed, reset, isUploading } = usePhotoBatchUpload();
  const { data: availableTags = [] } = useTags();
  const createTag = useCreateTag();

  // Reset the upload state when modal closes
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const handleFiles = (files: FileList) => {
    const newFiles = Array.from(files).filter(file => {
      // Only allow image files
      if (!file.type.startsWith('image/')) {
        return false;
      }
      // Check file size (max 10MB as per backend validation)
      if (file.size > 10 * 1024 * 1024) {
        return false;
      }
      return true;
    });
    
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateTag = async (name: string, color: string): Promise<TagType> => {
    return createTag.mutateAsync({ name, color });
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    const result = await uploadPhotos({
      jobId,
      files: selectedFiles,
      description: description.trim() || undefined,
      tagIds: tagIds.length > 0 ? tagIds : undefined,
    });

    // Only close if all uploads succeeded
    if (result.failed === 0) {
      setSelectedFiles([]);
      setDescription('');
      setTagIds([]);
      onClose();
    }
    // If some failed, keep modal open so user can retry
  };

  const handleRetry = async () => {
    const result = await retryFailed();

    // Close if retry succeeded
    if (result.failed === 0) {
      setSelectedFiles([]);
      setDescription('');
      setTagIds([]);
      onClose();
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFiles([]);
      setDescription('');
      setTagIds([]);
      reset();
      onClose();
    }
  };

  // Calculate progress percentage
  const progressPercent = progress.total > 0
    ? Math.round((progress.uploaded / progress.total) * 100)
    : 0;

  return (
    <Modal open={open} onClose={handleClose}>
      {/* Scrolls rather than pushing the upload button off a short screen. */}
      <ModalDialog sx={{ minWidth: 600, maxWidth: 800, maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography level="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Camera />
            Upload Photos {selectedFiles.length > 0 && `(${selectedFiles.length})`}
          </Typography>
          <IconButton
            variant="plain"
            color="neutral"
            onClick={handleClose}
            disabled={isUploading}
          >
            <X />
          </IconButton>
        </Box>

        {/* Upload Progress */}
        {isUploading && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography level="body-sm" fontWeight="lg">
                Uploading {progress.uploaded} of {progress.total} photos...
              </Typography>
              <Typography level="body-sm" color="neutral">
                {progressPercent}%
              </Typography>
            </Box>
            <LinearProgress
              determinate
              value={progressPercent}
              sx={{ height: 8, borderRadius: 4 }}
            />
            {progress.currentFile && (
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                Uploading: {progress.currentFile}
              </Typography>
            )}
          </Box>
        )}

        {/* Error Display */}
        {progress.status === 'error' && (
          <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
            <Typography level="body-sm">
              {progress.error || 'Failed to upload photos. Please try again.'}
            </Typography>
          </Alert>
        )}

        {/* Partial Failure Display */}
        {progress.status === 'partial_failure' && (
          <Alert color="warning" variant="soft" sx={{ mb: 3 }}>
            <Box>
              <Typography level="body-sm" fontWeight="lg">
                {progress.uploaded} of {progress.total} photos uploaded successfully.
              </Typography>
              <Typography level="body-sm">
                {progress.failed} photo{progress.failed !== 1 ? 's' : ''} failed to upload.
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Success Display */}
        {progress.status === 'completed' && progress.uploaded > 0 && (
          <Alert color="success" variant="soft" sx={{ mb: 3 }}>
            <Typography level="body-sm">
              Successfully uploaded {progress.uploaded} photo{progress.uploaded !== 1 ? 's' : ''}.
            </Typography>
          </Alert>
        )}

        {/* Drop Zone and Selected Files - Side by Side */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: { xs: 'wrap', sm: 'nowrap' }
        }}>
          {/* File Drop Zone */}
          <Box
            sx={{
              flex: 1,
              minWidth: 280,
              border: '2px dashed',
              borderColor: dragActive ? 'primary.main' : 'neutral.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: dragActive ? 'primary.50' : 'background.level1',
              transition: 'all 0.2s ease',
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={48} color="gray" style={{ marginBottom: 16 }} />
            <Typography level="body-lg" sx={{ mb: 1 }}>
              Drop photos here or click to browse
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Supports JPG, PNG, GIF up to 10MB each
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Box>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <Box sx={{
              width: { xs: '100%', sm: 280 },
              flexShrink: 0
            }}>
              <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                Selected Files ({selectedFiles.length}):
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                maxHeight: 200,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'sm',
                p: 1
              }}>
                {selectedFiles.map((file, index) => {
                  const status = getFileStatus(file, index, progress);
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        borderRadius: 'sm',
                        backgroundColor: status === 'failed' ? 'danger.softBg' : 'background.level1',
                      }}
                    >
                      {/* Status icon */}
                      {status === 'success' && <Check size={18} color="var(--joy-palette-success-500)" />}
                      {status === 'failed' && <AlertCircle size={18} color="var(--joy-palette-danger-500)" />}
                      {status === 'uploading' && <CircularProgress size="sm" sx={{ '--CircularProgress-size': '18px' }} />}
                      {status === 'pending' && <FileImage size={18} />}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="body-sm" noWrap>
                          {file.name}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                      {!isUploading && status !== 'success' && (
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          onClick={() => removeFile(index)}
                        >
                          <X size={16} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* Description Input */}
        <FormControl sx={{ mb: 3 }}>
          <FormLabel>Description (optional)</FormLabel>
          <Input
            placeholder="Add a description for these photos..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isUploading}
          />
        </FormControl>

        {/* Tags — applied to every photo in this upload, so a CoC or supplement
            report can pick the batch up without editing each photo afterwards */}
        <Box sx={{ mb: 3 }}>
          <TagPicker
            value={tagIds}
            onChange={setTagIds}
            availableTags={availableTags}
            onCreateTag={handleCreateTag}
            label="Tags (optional)"
            placeholder="Search or create tags..."
            helperText="Applied to all photos in this upload"
          />
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
          <Button
            variant="plain"
            onClick={handleClose}
            disabled={isUploading}
          >
            {progress.status === 'partial_failure' ? 'Close' : 'Cancel'}
          </Button>

          {/* Retry button for failed uploads */}
          {progress.status === 'partial_failure' && progress.failedFiles.length > 0 && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleRetry}
              disabled={isUploading}
              startDecorator={<RefreshCw size={18} />}
            >
              Retry Failed ({progress.failedFiles.length})
            </Button>
          )}

          {/* Main upload button - only show if not in partial failure state */}
          {progress.status !== 'partial_failure' && progress.status !== 'completed' && (
            <Button
              variant="solid"
              color="primary"
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || isUploading}
              startDecorator={isUploading ? <CircularProgress size="sm" /> : <Upload size={18} />}
            >
              {isUploading
                ? 'Uploading...'
                : `Upload ${selectedFiles.length} Photo${selectedFiles.length !== 1 ? 's' : ''}`
              }
            </Button>
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
};